<template>
  <div>
    <v-chart :option="options" :init-options="{ renderer: 'canvas' }" autoresize :style="{ height: `${height}px`, width: '100%' }"/>
    <div class="d-flex align-center flex-wrap caption grey--text text--darken-1 mt-1 dc-legend">
      <span class="dc-legend__swatch" :style="{ background: color }"></span>
      <span class="mr-4">{{ $t('components.dataCheck.DataCheckCoverageMap.higher') }}</span>
      <span class="dc-legend__swatch" :style="{ background: LOW_COLOR }"></span>
      <span class="mr-4">{{ $t('components.dataCheck.DataCheckCoverageMap.lower') }}</span>
      <span class="dc-legend__swatch dc-legend__swatch--empty"></span>
      <span>{{ $t('components.dataCheck.DataCheckCoverageMap.empty') }}</span>
    </div>
  </div>
</template>

<script>
import ECharts from 'vue-echarts'
import 'echarts/lib/chart/heatmap'
import 'echarts/lib/component/grid'
import 'echarts/lib/component/tooltip'
import 'echarts/lib/component/visualMapContinuous'
import 'echarts/lib/component/dataZoomSlider'
import 'echarts/lib/component/dataZoomInside'
import 'zrender/lib/canvas/canvas'
import { formatValue } from '@/utils/dataCheck'

const ROW_HEIGHT = 20
const MAX_VISIBLE_ROWS = 30
const LOW_COLOR = '#E8F1F6'

export default {
  props: {
    // [{ label, values }] with values aligned with periods (null = no data)
    rows: {
      type: Array,
      required: true
    },
    periods: {
      type: Array,
      required: true
    },
    color: {
      type: String,
      default: '#4DB6E7'
    }
  },
  data () {
    return {
      LOW_COLOR: LOW_COLOR
    }
  },
  computed: {
    scrolls () {
      return this.rows.length > MAX_VISIBLE_ROWS
    },
    height () {
      return Math.min(this.rows.length, MAX_VISIBLE_ROWS) * ROW_HEIGHT + 56
    },
    labels () {
      return this.rows.map(row => row.label)
    },
    // Each cell is its value relative to the largest absolute value of its row
    cells () {
      const cells = []
      this.rows.forEach((row, y) => {
        const max = row.values.reduce((m, v) => (v !== null && Math.abs(v) > m ? Math.abs(v) : m), 0)
        row.values.forEach((v, x) => {
          if (v === null) return
          cells.push([x, y, max > 0 ? Math.abs(v) / max : 0, v])
        })
      })
      return cells
    },
    options () {
      const options = {
        animation: false,
        grid: { left: 170, right: this.scrolls ? 28 : 8, top: 4, bottom: 44 },
        xAxis: {
          type: 'category',
          data: this.periods,
          splitArea: { show: false },
          axisTick: { show: false },
          axisLine: { lineStyle: { color: '#E0E0E0' } },
          axisLabel: { fontSize: 10, color: '#9E9E9E', hideOverlap: true }
        },
        yAxis: {
          type: 'category',
          data: this.labels,
          inverse: true,
          axisTick: { show: false },
          axisLine: { show: false },
          axisLabel: {
            fontSize: 11,
            color: '#616161',
            width: 160,
            overflow: 'truncate'
          }
        },
        visualMap: {
          show: false,
          min: 0,
          max: 1,
          dimension: 2,
          inRange: { color: [LOW_COLOR, this.color] }
        },
        tooltip: {
          formatter: this.formatTooltip
        },
        series: [{
          type: 'heatmap',
          data: this.cells,
          itemStyle: { borderColor: '#FFFFFF', borderWidth: 1 },
          emphasis: { itemStyle: { borderColor: '#424242', borderWidth: 1 } }
        }]
      }
      if (this.scrolls) {
        options.dataZoom = [
          { type: 'slider', yAxisIndex: 0, right: 4, width: 14, startValue: 0, endValue: MAX_VISIBLE_ROWS - 1, showDetail: false, brushSelect: false },
          { type: 'inside', yAxisIndex: 0, startValue: 0, endValue: MAX_VISIBLE_ROWS - 1, zoomOnMouseWheel: false, moveOnMouseWheel: true }
        ]
      }
      return options
    }
  },
  methods: {
    formatTooltip (param) {
      const value = param.value
      return `<div style="font-size: 12px; color: #757575">${this.periods[value[0]]}</div>` +
        `<div style="font-size: 13px">${this.labels[value[1]]}</div>` +
        `<div style="font-size: 14px"><b>${formatValue(value[3])}</b></div>`
    }
  },
  components: {
    'v-chart': ECharts
  }
}
</script>

<style scoped>
.dc-legend {
  gap: 4px;
}
.dc-legend__swatch {
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 2px;
  margin-right: 4px;
}
.dc-legend__swatch--empty {
  background: #FFFFFF;
  border: 1px solid #E0E0E0;
}
</style>
